import { useState, useRef, useMemo, useLayoutEffect } from "react";
import { useTheme } from "next-themes";
import { ChevronLeft, ChevronRight, ArrowRight, Search, X, AlertTriangle, ShieldCheck } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, Treemap, PieChart, Pie, ScatterChart, Scatter, ZAxis,
  XAxis, YAxis, Cell, Tooltip, ResponsiveContainer, CartesianGrid, LabelList, ReferenceLine, ReferenceArea,
  Rectangle,
} from "recharts";
import { DataTable, type Column } from "../../components/DataTable";
import { useElementWidth } from "../../components/ChartsComponent/chartUtils";
import type { AuditorAttribution, AuditorUnattributedSource } from "../../../api/frameworksApi";
import { Card, SectionTitle } from "../GRCManagerDashboard/grcDashboardWidgets";
import { DonutPanel } from "../GRCDashboardNew/grcDashboardHelpers";
import {
  complianceHex,
  riskLevel,
  severityHex,
  failedControlsTrendPct,
  repeatStreakBucket,
  CONTRIBUTOR_COLORS,
  PRIORITY_DRIVERS,
  REPEAT_STREAK_BUCKETS,
  type RepeatedControlFailure,
  ATTENTION_THRESHOLD,
  LOW_COVERAGE_THRESHOLD,
  NOT_MEASURED,
  UNMEASURED_HEX,
  type AttentionPriority,
  type PriorityDriver,
  type PriorityDriverKey,
  type OrgRiskRow,
  type ControlFailureRow,
  type FailureReasonRow,
  type PassFailSummary,
} from "./groupAuditorData";
import { Sheet, SheetContent } from "../../components/ui/sheet";
import { Tip } from "../../components/ui/tooltip";

const GREEN_HEX = "#10b981";
const GREEN_DARK_HEX = "#047857";

const RED_HEX = "#ef4444";

function toSortNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isNaN(v) ? null : v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function useTableSort<T>(
  rows: T[],
  accessors: Record<string, (row: T) => string | number | null> = {},
  searchOf?: (row: T) => string,
) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [query, setQuery] = useState("");

  const onSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const valueOf = (row: T, key: string): unknown =>
    accessors[key] ? accessors[key](row) : (row as Record<string, unknown>)[key];

  const q = query.trim().toLowerCase();
  const filtered =
    searchOf && q ? rows.filter((r) => searchOf(r).toLowerCase().includes(q)) : rows;

  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const av = valueOf(a, sortKey);
        const bv = valueOf(b, sortKey);
        const an = toSortNumber(av);
        const bn = toSortNumber(bv);
        const cmp =
          an !== null && bn !== null
            ? an - bn
            : String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      })
    : filtered;

  return { sortKey, sortDir, onSort, sorted, query, setQuery };
}

function DrawerSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative mt-3 w-64 max-w-full">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-indigo-400 focus:outline-none dark:focus:border-indigo-500/60"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Clear search"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}

const severityRank = (s: string) =>
  ({ Critical: 4, High: 3, Medium: 2, Low: 1, Unrated: 0 })[s] ?? 0;

function ChartTooltip({
  active,
  payload,
  suffix = "",
}: {
  active?: boolean;
  payload?: Array<{ payload: { name: string; fullName?: string; value?: number; size?: number } }>;
  suffix?: string;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const value = p.value ?? p.size ?? 0;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{p.fullName ?? p.name}</p>
      <p className="text-muted-foreground">
        <span className="font-semibold text-foreground">{value}{suffix}</span>
      </p>
    </div>
  );
}

function hexToRgb(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function lerpColor(from: string, to: string, t: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bch = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r}, ${g}, ${bch})`;
}
function rankGradient(index: number, total: number, from = "#ef4444", to = "#f59e0b"): string {
  const t = total > 1 ? index / (total - 1) : 0;
  return lerpColor(from, to, t);
}
function CountCell({ value }: { value: number }) {
  return <span className="text-sm font-semibold tabular-nums text-foreground/80">{value}</span>;
}

const PAGE_SIZE = 10;

function getPageWindow(page: number, totalPages: number, siblings = 1): (number | "dots")[] {
  const maxVisible = 2 * siblings + 5;
  if (totalPages <= maxVisible) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const current = page + 1;
  const out: (number | "dots")[] = [1];
  const start = Math.max(2, current - siblings);
  const end = Math.min(totalPages - 1, current + siblings);
  if (start > 2) out.push("dots");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < totalPages - 1) out.push("dots");
  out.push(totalPages);
  return out;
}

function RankedPaginationRow({
  page,
  total,
  pageSize = PAGE_SIZE,
  onPageChange,
  entityLabelPlural = "organizations",
}: {
  page: number;
  total: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  entityLabelPlural?: string;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  return (
    <div className="mt-2 flex items-center justify-between gap-2">
      <p className="text-xs text-muted-foreground">
        Showing <span className="font-semibold text-foreground/80">{start}-{end}</span> of{" "}
        <span className="font-semibold text-foreground/80">{total}</span> {entityLabelPlural.toLowerCase()}.
      </p>
      {totalPages > 1 && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            className="flex size-6 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ChevronLeft className="size-3.5" />
          </button>
          {getPageWindow(page, totalPages).map((p, idx) =>
            p === "dots" ? (
              <span key={`dots-${idx}`} className="flex size-6 items-center justify-center text-xs text-muted-foreground select-none">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p - 1)}
                className={`flex size-6 items-center justify-center rounded-md border text-xs font-semibold transition-colors ${
                  p - 1 === page
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(page + 1)}
            className="flex size-6 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ChevronRight className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function OrgNameCell({ name, active = true }: { name: string; active?: boolean }) {
  if (active) return <span className="text-left text-sm font-medium text-foreground">{name}</span>;
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-left">
      <span className="text-sm font-medium text-foreground">{name}</span>
      <Tip
        label="This application's org unit is deactivated. Its findings are still counted here because a decommissioned system with unresolved findings is itself an audit finding."
        side="top"
      >
        <span
          className="cursor-help rounded-md px-1.5 py-0.5 text-[11px] font-semibold"
          style={{ background: `${UNMEASURED_HEX}1f`, color: UNMEASURED_HEX }}
        >
          Deactivated
        </span>
      </Tip>
    </span>
  );
}

function ComplianceBarCell({ score }: { score: number | null }) {
  const c = complianceHex(score);
  if (score == null) {
    return (
      <Tip label="No published application under this entity produced a compliance score, so there is nothing to report here yet." side="top">
        <span className="mx-auto block w-24 cursor-help text-right text-xs font-medium text-muted-foreground">
          {NOT_MEASURED}
        </span>
      </Tip>
    );
  }
  return (
    <div className="mx-auto flex w-24 flex-col gap-1.5">
      <span className="text-right text-sm font-bold tabular-nums" style={{ color: c }}>
        {score}%
      </span>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, background: c }} />
      </div>
    </div>
  );
}

function coverageText(pct: number | null): string {
  return pct == null ? NOT_MEASURED : `${pct}%`;
}

function RiskBadge({ score }: { score: number }) {
  const { label, hex } = riskLevel(score);
  return (
    <span
      className="inline-flex min-w-[72px] items-center justify-center rounded-md px-2 py-0.5 text-xs font-semibold"
      style={{ background: `${hex}1f`, color: hex }}
    >
      {label}
    </span>
  );
}


function FailureRing({
  failed,
  total,
  hex,
  size = 148,
  id = "failed-controls-ring-chart",
  name = "Failed Controls Ring",
}: {
  failed: number;
  total: number | null | undefined;
  hex: string;
  size?: number;
  id?: string;
  name?: string;
}) {
  const stroke = 13;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const measured = typeof total === "number" && total > 0;
  const pct = measured ? Math.min(1, failed / total) : 0;
  return (
    <div id={id} data-name={name} className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-foreground/[0.08]"
        />
        {measured && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={hex}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${circumference * pct} ${circumference}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[34px] font-bold leading-none tabular-nums text-foreground">
          {failed}
        </span>
        <span className="mt-1 text-[11px] font-medium text-muted-foreground">
          {measured ? `of ${total} applicable` : "no applicable controls"}
        </span>
        {measured && (
          <span className="mt-0.5 text-[11px] font-bold tabular-nums" style={{ color: hex }}>
            {Math.round(pct * 100)}% failing
          </span>
        )}
      </div>
    </div>
  );
}

function DetailStatTile({
  label,
  value,
  hint,
  hex,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  hex?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <p className="text-[11px] font-bold tracking-wide text-muted-foreground">{label}</p>
      <div
        className="mt-1.5 text-[26px] font-bold leading-none tabular-nums text-foreground"
        style={hex ? { color: hex } : undefined}
      >
        {value}
      </div>
      {hint && <p className="mt-1.5 text-[11px] font-medium text-muted-foreground">{hint}</p>}
    </div>
  );
}

function DetailMetricRow({ label, value, hex }: { label: string; value: string; hex?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
      <span className="text-[13px] text-foreground/80">{label}</span>
      <span
        className="shrink-0 whitespace-nowrap text-[13px] font-bold tabular-nums text-foreground"
        style={hex ? { color: hex } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function EntityDetailBreakdown({
  row,
  trendLabel,
}: {
  row: OrgRiskRow;
  trendLabel: (pct: number | null) => string;
}) {
  const rl = riskLevel(row.riskScore);
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-border bg-card p-6 sm:flex-row sm:items-center">
        <FailureRing
          failed={row.failedControls}
          total={row.totalApplicableControls}
          hex={rl.hex}
        />
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
          <DetailStatTile
            label="Attention Priority"
            value={row.riskScore}
            hint="of 100"
            hex={rl.hex}
          />
          <DetailStatTile
            label="Compliance Score"
            value={row.complianceScore == null ? NOT_MEASURED : `${row.complianceScore}%`}
            hex={complianceHex(row.complianceScore)}
          />
          <DetailStatTile
            label="Risk Level"
            value={<SeverityBadge severity={rl.label} hex={rl.hex} />}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-[11px] font-bold tracking-wide text-muted-foreground">Findings</p>
          <div className="mt-1">
            <DetailMetricRow label="Total audit findings" value={String(row.totalFindings)} />
            <DetailMetricRow
              label="Critical findings"
              value={String(row.criticalFindings)}
              hex={row.criticalFindings > 0 ? RED_HEX : undefined}
            />
            <DetailMetricRow
              label="Critical controls failed"
              value={String(row.criticalControlsFailed)}
              hex={row.criticalControlsFailed > 0 ? RED_HEX : undefined}
            />
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-[11px] font-bold tracking-wide text-muted-foreground">
            Remediation &amp; assurance
          </p>
          <div className="mt-1">
            <DetailMetricRow
              label="Overdue remediation"
              value={String(row.overdueRemediation)}
              hex={row.overdueRemediation > 0 ? PENDING_HEX : undefined}
            />
            <DetailMetricRow label="Pending assessments" value={String(row.pendingAssessments)} />
            <DetailMetricRow
              label="Trend vs. previous quarter"
              value={trendLabel(failedControlsTrendPct(row))}
            />
            <DetailMetricRow
              label="Last audit"
              value={
                row.lastAuditDaysAgo == null
                  ? "No completed assessment"
                  : `${row.lastAuditDaysAgo} days ago`
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The "Unrated" severity value is kept as-is in data/type/sort logic — only
 *  the text shown to the user reads more plainly. */
function severityDisplayLabel(severity: string): string {
  return severity === "Unrated" ? "No Severity" : severity;
}

function SeverityBadge({ severity, hex }: { severity: string; hex?: string }) {
  const color = hex ?? severityHex(severity as ControlFailureRow["severity"]);
  return (
    <span
      className="inline-flex h-6 w-20 items-center justify-center whitespace-nowrap rounded-md px-2 text-xs font-semibold"
      style={{ background: `${color}1f`, color }}
    >
      {severityDisplayLabel(severity)}
    </span>
  );
}

const IMMEDIATE_ATTENTION_TOP_N = 7;
/** Tiles on the failure-rate heatmap. The full list is in its View More drawer. */
const HEATMAP_TOP_N = 10;

type ScoredRow = { row: OrgRiskRow; priority: AttentionPriority };



const LANDSCAPE_HEIGHT = 400;
const LANDSCAPE_MARGIN = { top: 12, right: 24, bottom: 28, left: 0 };
const LANDSCAPE_HIGHLIGHT_N = 5;
const LANDSCAPE_MAX_N = 10;

interface LandscapePoint {
  x: number;
  y: number;
  z: number;
  name: string;
  priority: number;
  coverage: number;
  band: string;
  hex: string;
  controls: number;
  isTop: boolean;
  row: OrgRiskRow;
}

function LandscapeTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: LandscapePoint }> }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{p.name}</p>
      <p className="mt-0.5 font-semibold" style={{ color: p.hex }}>
        Priority {p.priority} ({p.band})
      </p>
      <p className="text-muted-foreground">
        {p.coverage}% coverage of {p.controls} applicable controls
      </p>
    </div>
  );
}

function PriorityLandscape({
  allScored,
  onSelect,
  id = "priority-landscape-chart",
  name = "Priority vs Coverage Landscape",
}: {
  allScored: ScoredRow[];
  onSelect: (row: OrgRiskRow) => void;
  id?: string;
  name?: string;
}) {
  const ranked = allScored.slice(0, LANDSCAPE_MAX_N);
  const plottable = ranked.filter((s) => s.priority.coveragePct != null);

  const points: LandscapePoint[] = plottable
    .map(({ row, priority }, rank) => {
      const rl = riskLevel(priority.score);
      return {
        x: priority.coveragePct as number,
        y: priority.score,
        z: row.totalApplicableControls,
        name: row.name,
        priority: priority.score,
        coverage: priority.coveragePct as number,
        band: rl.label,
        hex: rl.hex,
        controls: row.totalApplicableControls,
        isTop: rank < LANDSCAPE_HIGHLIGHT_N,
        row,
      };
    })
    .reverse();

  return (
    <div id={id} data-name={name} className="mt-5">
      <div id={`${id}-plot`} data-name={`${name} plot`} className="w-full" style={{ height: LANDSCAPE_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={LANDSCAPE_MARGIN}>
            <CartesianGrid className="stroke-border" strokeOpacity={0.5} />
            <XAxis
              type="number"
              dataKey="x"
              domain={[0, 100]}
              ticks={[20, 40, LOW_COVERAGE_THRESHOLD, 80, 100]}
              tick={{ fontSize: 11, fontWeight: 600, fill: "currentColor" }}
              className="text-foreground/70"
              axisLine={false}
              tickLine={false}
              label={{ value: "Coverage %", position: "insideBottom", offset: -14, fontSize: 11, fill: "currentColor" }}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, 100]}
              ticks={[0, 20, ATTENTION_THRESHOLD, 60, 80, 100]}
              width={44}
              tick={{ fontSize: 11, fontWeight: 600, fill: "currentColor" }}
              className="text-foreground/70"
              axisLine={false}
              tickLine={false}
              label={{ value: "Priority index", angle: -90, position: "insideLeft", offset: 14, fontSize: 11, fill: "currentColor" }}
            />
            <ZAxis type="number" dataKey="z" range={[36, 340]} />

            <ReferenceArea
              x1={0}
              x2={LOW_COVERAGE_THRESHOLD}
              y1={ATTENTION_THRESHOLD}
              y2={100}
              fill={RED_HEX}
              fillOpacity={0.06}
              stroke="none"
            />
            <ReferenceLine x={LOW_COVERAGE_THRESHOLD} className="stroke-foreground/30" strokeWidth={1} />
            <ReferenceLine y={ATTENTION_THRESHOLD} className="stroke-foreground/30" strokeWidth={1} />

            <Tooltip content={<LandscapeTooltip />} cursor={{ strokeOpacity: 0 }} />

            <Scatter
              data={points}
              isAnimationActive={false}
              onClick={(p: unknown) => {
                const point = (p as { payload?: LandscapePoint })?.payload;
                if (point) onSelect(point.row);
              }}
              className="cursor-pointer"
            >
              {points.map((p) => (
                <Cell
                  key={p.row.id}
                  fill={p.hex}
                  fillOpacity={p.isTop ? 0.24 : 0.12}
                  stroke={p.hex}
                  strokeWidth={p.isTop ? 2.5 : 1.5}
                  strokeOpacity={p.isTop ? 1 : 0.7}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
}

interface WaterfallStep {
  name: string;
  base: number;
  value: number;
  hex: string;
  display: string;
  detail: string;
  isTotal?: boolean;
}

function buildWaterfall(priority: AttentionPriority): WaterfallStep[] {
  const steps: WaterfallStep[] = [];
  let running = 0;
  priority.drivers.forEach((d, i) => {
    steps.push({
      name: PRIORITY_DRIVERS[i]?.shortLabel ?? d.label,
      base: running,
      value: d.contribution,
      hex: d.hex,
      display: `+${d.contribution.toFixed(1)}`,
      detail: d.raw,
    });
    running += d.contribution;
  });

  const final = priority.score;
  steps.push({
    name: "Priority",
    base: 0,
    value: final,
    hex: riskLevel(final).hex,
    display: String(final),
    detail: riskLevel(final).label,
    isTotal: true,
  });
  return steps;
}

function WaterfallTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: WaterfallStep }> }) {
  if (!active || !payload?.length) return null;
  const s = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{s.name}</p>
      <p className="text-muted-foreground">{s.detail}</p>
      <p className="mt-0.5 font-semibold" style={{ color: s.hex }}>{s.display}</p>
    </div>
  );
}

const DERIVATION_COLUMNS: Column<PriorityDriver>[] = [
  {
    key: "label",
    header: "Driver",
    align: "left",
    headerAlign: "center",
    render: (d) => (
      <span className="flex items-center gap-2">
        <span className="size-2.5 shrink-0 rounded-full" style={{ background: d.hex }} />
        <span className="font-medium text-foreground">{d.label}</span>
      </span>
    ),
  },
  {
    key: "raw",
    header: "Input",
    align: "left",
    headerAlign: "center",
    render: (d) => <span className="text-muted-foreground">{d.raw}</span>,
  },
  {
    key: "weight",
    header: "Weight",
    render: (d) => (
      <span className="tabular-nums text-foreground/80">{Math.round(d.weight * 100)}%</span>
    ),
  },
  {
    key: "contribution",
    header: "Points",
    render: (d) => (
      <span className="font-semibold tabular-nums" style={{ color: d.hex }}>
        +{d.contribution.toFixed(1)}
      </span>
    ),
  },
];

function PriorityWaterfall({
  priority,
  id = "priority-waterfall-chart",
  name = "Priority Score Breakdown",
}: {
  priority: AttentionPriority;
  id?: string;
  name?: string;
}) {
  const steps = buildWaterfall(priority);
  return (
    <div id={id} data-name={name} className="w-full" style={{ height: steps.length * 38 + 40 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={steps} layout="vertical" margin={{ top: 4, right: 48, left: 4, bottom: 4 }} barCategoryGap="18%">
          <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border" />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "currentColor" }} className="text-muted-foreground" axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" width={104} tick={{ fontSize: 11, fill: "currentColor" }} className="text-foreground" axisLine={false} tickLine={false} interval={0} />
          <Tooltip content={<WaterfallTooltip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
          <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="value" stackId="wf" maxBarSize={22} radius={[0, 3, 3, 0]}>
            {steps.map((s, i) => (
              <Cell key={i} fill={s.hex} fillOpacity={s.isTotal ? 1 : 0.85} />
            ))}
            <LabelList
              dataKey="display"
              position="right"
              fontSize={11}
              fontWeight={700}
              className="fill-foreground"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function driverCellText(key: PriorityDriverKey, row: OrgRiskRow): string {
  switch (key) {
    case "complianceGap":
      return row.complianceScore == null ? "–" : `${row.complianceScore}%`;
    case "criticalFindings":
      return String(row.criticalFindingsUnmanaged);
    case "failedControlDensity":
      return row.totalApplicableControls > 0
        ? `${Math.round((row.failedControls / row.totalApplicableControls) * 100)}%`
        : "–";
    case "overdueRemediation":
      return String(row.overdueRemediation);
  }
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function cellTint(hex: string, normalized: number) {
  const t = clamp01(normalized / 100);
  return {
    background: rgba(hex, 0.08 + t * 0.84),
    color: normalized >= 55 ? "#ffffff" : undefined,
  };
}
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const MATRIX_COLUMNS = `minmax(190px, 1.6fr) repeat(${PRIORITY_DRIVERS.length}, minmax(76px, 88px)) minmax(96px, 108px)`;

function DriverMatrix({
  rows,
  allScored,
  entityLabel,
  onSelect,
  id = "priority-driver-matrix-chart",
  name = "Priority Driver Matrix",
}: {
  rows: ScoredRow[];
  allScored: ScoredRow[];
  entityLabel: string;
  onSelect: (row: OrgRiskRow) => void;
  id?: string;
  name?: string;
}) {
  const estateAverage = (index: number) =>
    allScored.length
      ? allScored.reduce((sum, s) => sum + (s.priority.drivers[index]?.normalized ?? 0), 0) / allScored.length
      : 0;

  return (
    <div id={id} data-name={name} className="mt-3 overflow-x-auto">
      <div className="min-w-[720px]">
        <div className="grid items-end gap-1 pb-2" style={{ gridTemplateColumns: MATRIX_COLUMNS }}>
          <span className="text-xs font-semibold text-muted-foreground">{entityLabel}</span>
          {PRIORITY_DRIVERS.map((d) => (
            <Tip key={d.key} label={`${d.label}: ${d.description}. Weight ${Math.round(d.weight * 100)}% of the index.`} side="top" className="flex justify-center">
              <span className="flex cursor-help flex-col items-center gap-0.5 text-center">
                <span className="size-2 rounded-full" style={{ background: d.hex }} />
                <span className="flex flex-col text-[11px] font-semibold leading-tight text-foreground/80">
                  {d.headerLines.map((line) => (
                    <span key={line} className="whitespace-nowrap">{line}</span>
                  ))}
                </span>
                <span className="text-[10px] text-muted-foreground">{Math.round(d.weight * 100)}%</span>
              </span>
            </Tip>
          ))}
          <span className="text-center text-xs font-semibold text-muted-foreground">Priority</span>
        </div>

        <div className="flex flex-col gap-1">
          {rows.map(({ row, priority }) => {
            const rl = riskLevel(priority.score);
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => onSelect(row)}
                className="grid items-center gap-1 rounded-lg py-1 text-left transition-colors hover:bg-muted/50"
                style={{ gridTemplateColumns: MATRIX_COLUMNS }}
              >
                <span className="flex min-w-0 flex-col gap-1 pr-2">
                  <span className="text-sm font-medium text-foreground">{row.name}</span>
                  <RowFlags priority={priority} entityLabel={entityLabel} unmanagedCritical={row.criticalFindingsUnmanaged} />
                </span>
                {priority.drivers.map((d) => (
                  <Tip key={d.key} label={`${d.label}: ${d.raw}, contributes ${d.contribution.toFixed(1)} of ${priority.score}`} side="top" className="flex">
                    <span
                      className="flex h-9 w-full cursor-help items-center justify-center rounded-md text-sm font-semibold tabular-nums text-foreground/80"
                      style={cellTint(d.hex, d.normalized)}
                    >
                      {driverCellText(d.key, row)}
                    </span>
                  </Tip>
                ))}
                <span className="flex items-center justify-center gap-2">
                  <span className="text-lg font-bold tabular-nums leading-none" style={{ color: rl.hex }}>
                    {priority.score}
                  </span>
                  <span
                    className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                    style={{ background: `${rl.hex}1f`, color: rl.hex }}
                  >
                    {rl.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-2 grid items-center gap-1 border-t border-border pt-2" style={{ gridTemplateColumns: MATRIX_COLUMNS }}>
          <span className="pr-2 text-xs font-semibold text-muted-foreground">
            All {allScored.length} {entityLabel.toLowerCase()}s, average
          </span>
          {PRIORITY_DRIVERS.map((d, i) => {
            const avg = estateAverage(i);
            return (
              <Tip key={d.key} label={`Average ${d.label.toLowerCase()} severity across all ${allScored.length} ${entityLabel.toLowerCase()}s`} side="top" className="flex">
                <span
                  className="flex h-7 w-full cursor-help items-center justify-center rounded-md text-xs font-semibold tabular-nums text-foreground/70"
                  style={cellTint(d.hex, avg)}
                >
                  {Math.round(avg)}
                </span>
              </Tip>
            );
          })}
          <span className="text-center text-xs text-muted-foreground">of 100</span>
        </div>
      </div>
    </div>
  );
}

const BADGE_MD = "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-semibold";
const BADGE_SM = "inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold";

function FlagChip({
  label,
  hex,
  tip,
  size = "sm",
}: {
  label: string;
  hex: string;
  tip: string;
  size?: "sm" | "md";
}) {
  return (
    <Tip label={tip} side="top">
      <span className={size === "md" ? BADGE_MD : BADGE_SM} style={{ background: `${hex}1f`, color: hex }}>
        <AlertTriangle className={size === "md" ? "size-3.5" : "size-3"} />
        {label}
      </span>
    </Tip>
  );
}

function RowFlags({
  priority,
  entityLabel,
  unmanagedCritical,
  size = "sm",
}: {
  priority: AttentionPriority;
  entityLabel: string;
  unmanagedCritical?: number;
  size?: "sm" | "md";
}) {
  if (!priority.mustAct && !priority.lowCoverage) return null;
  return (
    <span className={`flex flex-wrap items-center ${size === "md" ? "gap-2" : "gap-1"}`}>
      {priority.mustAct && (
        <FlagChip
          size={size}
          label={
            unmanagedCritical && unmanagedCritical > 0
              ? `${unmanagedCritical} unmanaged critical`
              : "Unmanaged critical"
          }
          hex="#ef4444"
          tip={`This ${entityLabel.toLowerCase()} has ${
            unmanagedCritical && unmanagedCritical > 0 ? unmanagedCritical : "open"
          } critical finding${unmanagedCritical === 1 ? "" : "s"} with no active remediation plan.`}
        />
      )}
      {priority.lowCoverage && (
        <FlagChip
          size={size}
          label={priority.coveragePct == null ? "No coverage data" : `${priority.coveragePct}% coverage`}
          hex="#f59e0b"
          tip={
            priority.coveragePct == null
              ? `This ${entityLabel.toLowerCase()} has no applicable controls, so nothing has been measured. The priority index reflects only its findings and remediation backlog.`
              : `Only ${priority.coveragePct}% of applicable controls were covered by the latest assessment. Unassessed controls never reduce the compliance score, so this priority may be understated.`
          }
        />
      )}
    </span>
  );
}

function PriorityScoreCell({ score }: { score: number }) {
  const { hex } = riskLevel(score);
  return (
    <div className="mx-auto flex w-20 flex-col gap-1.5">
      <span className="text-right text-sm font-bold tabular-nums" style={{ color: hex }}>{score}</span>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, background: hex }} />
      </div>
    </div>
  );
}

function PriorityBandStrip({
  rows,
  entityLabelPlural,
  id = "priority-band-strip",
  name = "Priority Band Strip",
}: {
  rows: Array<{ priority: AttentionPriority }>;
  entityLabelPlural: string;
  id?: string;
  name?: string;
}) {
  const bands = [
    { label: "Critical", hex: "#ef4444", min: 75 },
    { label: "High", hex: "#f97316", min: 60 },
    { label: "Medium", hex: "#f59e0b", min: ATTENTION_THRESHOLD },
    { label: "Low", hex: "#64748b", min: 0 },
  ];
  const counts = bands.map((b, i) => {
    const max = i === 0 ? 101 : bands[i - 1].min;
    return { ...b, count: rows.filter((r) => r.priority.score >= b.min && r.priority.score < max).length };
  });
  const mustAct = rows.filter((r) => r.priority.mustAct).length;
  return (
    <div id={id} data-name={name} className="mt-1 flex flex-wrap items-center gap-2">
      {counts.map((b) => (
        <span
          key={b.label}
          className="inline-flex items-center gap-1.5 rounded-lg border border-transparent px-2.5 py-1 text-xs font-semibold leading-5"
          style={{ background: `${b.hex}14`, color: b.hex }}
        >
          <span className="font-bold tabular-nums">{b.count}</span>
          {b.label}
        </span>
      ))}
      <Tip
        label={`${entityLabelPlural} with at least one open critical finding that has no active remediation plan. These stay on the worklist regardless of their priority index.`}
        side="top"
      >
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-2.5 py-1 text-xs font-semibold leading-5 text-muted-foreground">
          <span className="font-bold tabular-nums text-foreground">{mustAct}</span>
          Unmanaged critical
        </span>
      </Tip>
    </div>
  );
}

const UNATTRIBUTED_REASON_TEXT: Record<AuditorUnattributedSource["reason"], string> = {
  MISSING_SSP: "References an SSP that no longer exists",
  NOT_AN_APPLICATION: "Belongs to a service, not an application",
  UNPUBLISHED_OR_OUT_OF_SCOPE: "SSP is not published, or sits outside this scope",
  NO_REFERENCE: "Document carries no SSP reference",
  OUTSIDE_EVERY_ORGANIZATION: "Owned outside every organization in this group",
};

export function AttributionNotice({
  attribution,
  entityLabel,
  scopeName,
}: {
  attribution: AuditorAttribution | null;
  entityLabel: string;
  scopeName: string;
}) {
  const [open, setOpen] = useState(false);
  if (!attribution) return null;

  const lostFindings = Math.max(0, attribution.totalFindings - attribution.attributedFindings);
  const lostAssessments = Math.max(0, attribution.totalAssessments - attribution.attributedAssessments);
  if (lostFindings === 0 && lostAssessments === 0) return null;

  const columns: Column<AuditorUnattributedSource>[] = [
    {
      key: "label",
      header: "Source",
      align: "left",
      headerAlign: "center",
      render: (s) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{s.label}</p>
          {s.reference && (
            <p className="text-xs text-muted-foreground">{s.reference}</p>
          )}
        </div>
      ),
    },
    {
      key: "reason",
      header: "Why",
      align: "left",
      headerAlign: "center",
      render: (s) => (
        <span className="text-sm text-foreground/80">{UNATTRIBUTED_REASON_TEXT[s.reason] ?? s.reason}</span>
      ),
    },
    { key: "findings", header: "Findings", render: (s) => <CountCell value={s.findings} /> },
    { key: "assessments", header: "Assessments", render: (s) => <CountCell value={s.assessments} /> },
  ];

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 dark:border-amber-500/25 dark:bg-amber-500/[0.06]">
      <div className="flex min-w-0 items-start gap-2.5">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 text-xs leading-relaxed">
          <p className="font-semibold text-foreground">
            Not everything in {scopeName} is accounted for below
          </p>
          <p className="text-muted-foreground">
            {attribution.totalFindings} open findings and {attribution.totalAssessments} assessment plans in scope.
            {" "}
            <span className="font-semibold text-foreground">
              {attribution.attributedFindings} findings
            </span>{" "}
            and{" "}
            <span className="font-semibold text-foreground">
              {attribution.attributedAssessments} assessments
            </span>{" "}
            are attributed to the {entityLabel.toLowerCase()}s below;{" "}
            <span className="font-semibold text-amber-700 dark:text-amber-400">
              {lostFindings} findings
            </span>{" "}
            and{" "}
            <span className="font-semibold text-amber-700 dark:text-amber-400">
              {lostAssessments} assessments
            </span>{" "}
            are not.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 rounded-full border border-amber-400/60 px-3.5 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-500/10"
      >
        Show what is missing
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[960px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-lg font-semibold text-foreground">Unattributed evidence</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {lostFindings} open findings and {lostAssessments} assessment plans belong to {scopeName} but reach no{" "}
              {entityLabel.toLowerCase()} row. Each line is a fix at source, not a display problem.
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <DataTable<AuditorUnattributedSource>
              columns={columns}
              data={attribution.sources}
              rowKey={(s) => `${s.reason}:${s.reference ?? "none"}`}
              emptyText="No Data Available"
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function ImmediateAttentionSection({
  allRows,
  entityLabel = "Organization",
  title,
  subtitle,
}: {
  allRows: OrgRiskRow[];
  entityLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const entityLabelPlural = `${entityLabel}s`;
  const [selectedOrg, setSelectedOrg] = useState<OrgRiskRow | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(0);
  const [viewMode] = useState<"chart" | "matrix" | "table">("chart");

  const scored = allRows
    .map((row) => ({ row, priority: row.priority }))
    .sort((a, b) => b.priority.score - a.priority.score);
  const needingAttention = scored.filter((s) => s.priority.needsAttention);
  /* Falls back to the overall ranking when nothing crosses the threshold, for
     the same reason the chart does — an empty panel is worse than the ranked
     list it would otherwise hide. */
  const top = (needingAttention.length > 0 ? needingAttention : scored).slice(
    0,
    IMMEDIATE_ATTENTION_TOP_N,
  );
  const topRows = top.map((s) => s.row);
  const priorityByRowId = new Map(scored.map((s) => [s.row.id, s.priority]));

  const selectedPriority = selectedOrg ? priorityByRowId.get(selectedOrg.id) ?? selectedOrg.priority : null;

  const drawerSource = scored.map((s) => s.row);
  const drawerRows = drawerSource.slice(drawerPage * PAGE_SIZE, drawerPage * PAGE_SIZE + PAGE_SIZE);
  const drawerColumns: Column<OrgRiskRow>[] = [
    { key: "name", header: entityLabel, align: "left", headerAlign: "center", render: (r) => (
      <div className="flex flex-col items-start gap-1">
        <OrgNameCell name={r.name} active={r.active} />
        <RowFlags priority={priorityByRowId.get(r.id) ?? r.priority} entityLabel={entityLabel} unmanagedCritical={r.criticalFindingsUnmanaged} />
      </div>
    ) },
    { key: "riskScore", header: "Priority", render: (r) => <PriorityScoreCell score={r.riskScore} /> },
    { key: "complianceScore", header: "Compliance Score", render: (r) => <ComplianceBarCell score={r.complianceScore} /> },
    /* whitespace-nowrap on both ratio cells: the app-wide word-break rule
       otherwise splits "406 / 1402" across two lines in a narrow column. */
    { key: "criticalFindings", header: "Critical Findings", render: (r) => (
      <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-foreground/80">
        {r.criticalFindingsUnmanaged}
        <span className="text-muted-foreground"> / {r.criticalFindings}</span>
      </span>
    ) },
    { key: "failedControls", header: "Not Satisfied", render: (r) => (
      <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-foreground/80">
        {r.failedControls}
        <span className="text-muted-foreground"> / {r.totalApplicableControls}</span>
      </span>
    ) },
    { key: "overdueRemediation", header: "Overdue Remediation", render: (r) => <CountCell value={r.overdueRemediation} /> },
    { key: "coverage", header: "Coverage", render: (r) => {
      const pct = r.priority.coveragePct;
      if (pct == null) {
        return (
          <span
            className="text-xs font-medium text-muted-foreground"
            title="No applicable controls, so assessment coverage is undefined. This is not 0% and not 100%."
          >
            {NOT_MEASURED}
          </span>
        );
      }
      const thin = pct < LOW_COVERAGE_THRESHOLD;
      return (
        <span
          className={`text-sm font-semibold tabular-nums ${thin ? "text-amber-600 dark:text-amber-400" : "text-foreground/80"}`}
          title={thin ? `Only ${pct}% of applicable controls were assessed — this priority is understated.` : undefined}
        >
          {pct}%
        </span>
      );
    } },
    { key: "riskLevel", header: "Risk Level", render: (r) => <RiskBadge score={r.riskScore} /> },
  ];

  const tableColumns: Column<OrgRiskRow>[] = drawerColumns.map((c) => ({ ...c, sortable: true }));

  const tableSort = useTableSort(
    scored.map((s) => s.row),
    {
      riskScore: (r) => r.riskScore,
      riskLevel: (r) => r.riskScore,
      coverage: (r) => r.priority.coveragePct,
      criticalFindings: (r) => r.criticalFindingsUnmanaged,
      failedControls: (r) => r.failedControls,
    },
  );

  const sectionTitle = title ?? `${entityLabelPlural} Requiring Immediate Attention`;

  return (
    <Card id="immediate-attention-section" name={sectionTitle} className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <SectionTitle
          title={sectionTitle}
          subtitle={
            subtitle ??
            `${entityLabelPlural} ranked by attention priority based on compliance risk, control coverage, and unmanaged critical findings.`
          }
        />
        <div className="flex shrink-0 items-center gap-2">
          <ViewMoreButton onClick={() => setViewAllOpen(true)} />
        </div>
      </div>

      {scored.length === 0 ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 py-10 text-center">
          <ShieldCheck className="size-8" style={{ color: GREEN_HEX }} />
          <p className="text-sm font-semibold text-foreground">No {entityLabelPlural.toLowerCase()} in scope</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Nothing to rank for the current selection.
          </p>
        </div>
      ) : viewMode === "chart" ? (
        <>
          <PriorityBandStrip rows={scored} entityLabelPlural={entityLabelPlural} />
          <PriorityLandscape allScored={scored} onSelect={setSelectedOrg} />
        </>
      ) : viewMode === "matrix" ? (
        <>
          <PriorityBandStrip rows={scored} entityLabelPlural={entityLabelPlural} />
          <DriverMatrix
            rows={top}
            allScored={scored}
            entityLabel={entityLabel}
            onSelect={setSelectedOrg}
          />
        </>
      ) : (
        <div className="mt-1">
          <PriorityBandStrip rows={scored} entityLabelPlural={entityLabelPlural} />
          <div className="mt-3">
            <DataTable<OrgRiskRow>
              columns={tableColumns}
              data={tableSort.sorted}
              rowKey={(r) => r.id}
              emptyText="No Data Available"
              onRowClick={(r) => setSelectedOrg(r)}
              sortKey={tableSort.sortKey}
              sortDir={tableSort.sortDir}
              onSort={tableSort.onSort}
            />
          </div>
        </div>
      )}

      <Sheet open={!!selectedOrg} onOpenChange={(open) => !open && setSelectedOrg(null)}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[780px]">
          {selectedOrg && selectedPriority && (
            <>
              <div className="shrink-0 border-b border-border p-6">
                <h2 className="text-xl font-bold text-foreground">{selectedOrg.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">How this priority index was derived</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span
                    className={BADGE_MD}
                    style={{ background: `${riskLevel(selectedPriority.score).hex}1f`, color: riskLevel(selectedPriority.score).hex }}
                  >
                    <span className="text-sm font-bold tabular-nums">{selectedPriority.score}</span>
                    {riskLevel(selectedPriority.score).label}
                  </span>
                  <RowFlags priority={selectedPriority} entityLabel={entityLabel} unmanagedCritical={selectedOrg.criticalFindingsUnmanaged} size="md" />
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Score build-up</p>
                <PriorityWaterfall priority={selectedPriority} />

                <p className="mb-2 mt-4 text-xs font-semibold text-muted-foreground">
                  Where each driver came from
                </p>
                <DataTable<PriorityDriver>
                  columns={DERIVATION_COLUMNS}
                  data={selectedPriority.drivers}
                  rowKey={(d) => d.key}
                  emptyText="No drivers"
                />

              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[95vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1440px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-xl font-bold text-foreground">{title ?? `${entityLabelPlural} Requiring Immediate Attention`}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Detailed priority ranking of all {entityLabelPlural.toLowerCase()}, including compliance score,
              findings, remediation status, and coverage metrics.
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <DataTable<OrgRiskRow>
              columns={drawerColumns}
              data={drawerRows}
              rowKey={(r) => r.id}
              emptyText="No Data Available"
              page={drawerPage}
              pageSize={PAGE_SIZE}
              totalItems={drawerSource.length}
              onPageChange={setDrawerPage}
              itemLabel={entityLabelPlural.toLowerCase()}
            />
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}


const AXIS_LABEL_MAX_LINES = 3;
const AXIS_LABEL_FONT_MAX = 11;
const AXIS_LABEL_FONT_MIN = 8;
const AXIS_LABEL_GUTTER = 10;
const AXIS_TITLE_SPACE = 18;

let axisMeasureCtx: CanvasRenderingContext2D | null | undefined;
let axisFontFamily: string | undefined;
function measureAxisText(text: string, fontSize: number): number {
  if (axisMeasureCtx === undefined) {
    axisMeasureCtx = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  }
  if (!axisMeasureCtx) return text.length * fontSize * 0.58;
  if (axisFontFamily === undefined) {
    axisFontFamily = (document.body && getComputedStyle(document.body).fontFamily) || "system-ui, sans-serif";
  }
  axisMeasureCtx.font = `600 ${fontSize}px ${axisFontFamily}`;
  return axisMeasureCtx.measureText(text).width;
}

function wrapAxisLabel(name: string, maxWidth: number, fontSize: number): string[] {
  const words = (name ?? "").split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const candidate = cur ? `${cur} ${w}` : w;
    if (!cur || measureAxisText(candidate, fontSize) <= maxWidth) cur = candidate;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function clipAxisText(text: string, maxWidth: number, fontSize: number): string {
  if (measureAxisText(text, fontSize) <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && measureAxisText(`${out}...`, fontSize) > maxWidth) out = out.slice(0, -1);
  return `${out.replace(/[.\s]+$/, "")}...`;
}

type AxisLabelLayout = {
  mode: "stacked" | "tilted";
  fontSize: number;
  lineHeight: number;
  firstLineOffset: number;
  height: number;
  linesFor: (name: string) => string[];
};

const TILT_ANGLE = 60;
const TILT_SIN = Math.sin((TILT_ANGLE * Math.PI) / 180);
const TILT_COS = Math.cos((TILT_ANGLE * Math.PI) / 180);
const TILT_TEXT_MIN = 70;
const TILT_TEXT_MAX = 140;

function wrapFits(names: string[], maxWidth: number, fontSize: number, maxLines: number): string[][] | null {
  const wrapped = names.map((n) => wrapAxisLabel(n, maxWidth, fontSize));
  const ok = wrapped.every(
    (lines) => lines.length <= maxLines && lines.every((l) => measureAxisText(l, fontSize) <= maxWidth),
  );
  return ok ? wrapped : null;
}

function clampLines(name: string, maxWidth: number, fontSize: number): string[] {
  const wrapped = wrapAxisLabel(name, maxWidth, fontSize);
  const kept = wrapped.slice(0, AXIS_LABEL_MAX_LINES).map((l) => clipAxisText(l, maxWidth, fontSize));
  if (wrapped.length > AXIS_LABEL_MAX_LINES && kept.length) {
    kept[kept.length - 1] = clipAxisText(`${kept[kept.length - 1]}...`, maxWidth, fontSize);
  }
  return kept.length ? kept : [""];
}

function orgAxisLabelLayout(names: string[], bandWidth: number): AxisLabelLayout {
  const lineHeightOf = (fontSize: number) => Math.round(fontSize * 1.2 * 10) / 10;
  const stacked = (fontSize: number, maxWidth: number): AxisLabelLayout => {
    const lineHeight = lineHeightOf(fontSize);
    const firstLineOffset = fontSize + 2;
    const linesFor = (name: string) => clampLines(name, maxWidth, fontSize);
    const maxLines = Math.max(1, ...names.map((n) => linesFor(n).length));
    return {
      mode: "stacked",
      fontSize,
      lineHeight,
      firstLineOffset,
      height: Math.ceil(firstLineOffset + (maxLines - 1) * lineHeight + AXIS_TITLE_SPACE),
      linesFor,
    };
  };

  if (!names.length) return stacked(AXIS_LABEL_FONT_MAX, 100);

  const bandTextWidth = Math.max(24, bandWidth - AXIS_LABEL_GUTTER);
  for (let f = AXIS_LABEL_FONT_MAX; f >= AXIS_LABEL_FONT_MIN; f -= 0.5) {
    if (wrapFits(names, bandTextWidth, f, AXIS_LABEL_MAX_LINES)) return stacked(f, bandTextWidth);
  }

  const tilted = (fontSize: number, textWidth: number): AxisLabelLayout => {
    const lineHeight = lineHeightOf(fontSize);
    const firstLineOffset = fontSize + 2;
    const linesFor = (name: string) => clampLines(name, textWidth, fontSize);
    const allLines = names.map((n) => linesFor(n));
    const maxLines = Math.max(1, ...allLines.map((l) => l.length));
    const widest = Math.max(
      0,
      ...allLines.flat().map((l) => measureAxisText(l, fontSize)),
    );
    const drop = TILT_SIN * widest + TILT_COS * (firstLineOffset + (maxLines - 1) * lineHeight);
    return {
      mode: "tilted",
      fontSize,
      lineHeight,
      firstLineOffset,
      height: Math.ceil(drop + 6 + AXIS_TITLE_SPACE),
      linesFor,
    };
  };

  for (let f = AXIS_LABEL_FONT_MAX; f >= AXIS_LABEL_FONT_MIN; f -= 0.5) {
    const allowedLines = Math.max(1, Math.floor((bandWidth * TILT_SIN) / lineHeightOf(f)));
    const lineBudget = Math.min(AXIS_LABEL_MAX_LINES, allowedLines);
    for (let w = TILT_TEXT_MIN; w <= TILT_TEXT_MAX; w += 10) {
      if (wrapFits(names, w, f, lineBudget)) return tilted(f, w);
    }
  }

  return tilted(AXIS_LABEL_FONT_MIN, TILT_TEXT_MAX);
}

function OrgNameAxisTick({
  x = 0,
  y = 0,
  payload,
  layout,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  layout?: AxisLabelLayout;
}) {
  const name = payload?.value ?? "";
  const lines = layout ? layout.linesFor(name) : [name];
  const fontSize = layout?.fontSize ?? AXIS_LABEL_FONT_MAX;
  const lineHeight = layout?.lineHeight ?? 12.5;
  const firstLineOffset = layout?.firstLineOffset ?? 13;
  const tilted = layout?.mode === "tilted";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor={tilted ? "end" : "middle"}
        transform={tilted ? `rotate(-${TILT_ANGLE})` : undefined}
        className="fill-black dark:fill-white"
        fontSize={fontSize}
        fontWeight={600}
      >
        <title>{name}</title>
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? firstLineOffset : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}


const COMPLIANCE_BANDS: Array<{ label: string; min: number; max: number; hex: string }> = [
  { label: "0 - 40%", min: 0, max: 40, hex: RED_HEX },
  { label: "41 - 60%", min: 41, max: 60, hex: "#f97316" },
  { label: "61 - 80%", min: 61, max: 80, hex: GREEN_HEX },
  { label: "81 - 100%", min: 81, max: 100, hex: GREEN_DARK_HEX },
];

export function LowestComplianceSection({
  allRows,
  entityLabel = "Organization",
  scopeLabel = "the group",
  title,
  subtitle,
}: {
  allRows: OrgRiskRow[];
  entityLabel?: string;
  scopeLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const entityLabelPlural = `${entityLabel}s`;
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(0);

  const bandFor = (score: number) =>
    COMPLIANCE_BANDS.find((b) => score >= b.min && score <= b.max) ?? COMPLIANCE_BANDS[0];

  const measured = allRows.filter((r) => r.complianceScore != null);
  const unmeasuredCount = allRows.length - measured.length;
  const barData = [...measured]
    .sort((a, b) => (a.complianceScore as number) - (b.complianceScore as number))
    .slice(0, 5)
    .map((r) => ({
      name: r.name,
      short: r.name.replace(/\s*\([^)]*\)\s*$/, "").trim(),
      compliance: r.complianceScore as number,
      color: bandFor(r.complianceScore as number).hex,
      band: bandFor(r.complianceScore as number).label,
      criticalFindings: r.criticalFindings,
      applicableControls: r.totalApplicableControls,
      coveragePct: r.priority.coveragePct,
    }));

  const byComplianceAsc = [...allRows].sort((a, b) => {
    if (a.complianceScore == null || b.complianceScore == null) {
      return (a.complianceScore == null ? 1 : 0) - (b.complianceScore == null ? 1 : 0);
    }
    return a.complianceScore - b.complianceScore;
  });
  const sort = useTableSort(byComplianceAsc, { riskLevel: (r) => r.riskScore }, (r) => r.name);
  const drawerRows = sort.sorted.slice(drawerPage * PAGE_SIZE, drawerPage * PAGE_SIZE + PAGE_SIZE);
  const drawerColumns: Column<OrgRiskRow>[] = [
    { key: "name", header: entityLabel, align: "left", headerAlign: "center", sortable: true, render: (r) => <OrgNameCell name={r.name} active={r.active} /> },
    { key: "complianceScore", header: "Compliance Score", sortable: true, render: (r) => <ComplianceBarCell score={r.complianceScore} /> },
    { key: "criticalFindings", header: "Critical Findings", sortable: true, render: (r) => <CountCell value={r.criticalFindings} /> },
    { key: "riskLevel", header: "Risk Level", sortable: true, render: (r) => <RiskBadge score={r.riskScore} /> },
  ];

  const sectionTitle = title ?? `${entityLabelPlural} with Lowest Compliance`;

  return (
    <Card id="lowest-compliance-section" name={sectionTitle} className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2">
        <SectionTitle
          title={sectionTitle}
          subtitle={subtitle ?? `Satisfied controls as a share of those assessed, across ${scopeLabel}`}
        />
        <ViewMoreButton onClick={() => setViewAllOpen(true)} />
      </div>
      <div
        id="lowest-compliance-chart-legend"
        data-name={`${sectionTitle} legend`}
        className="-mt-1 mb-1 flex items-center gap-4 text-xs"
      >
        <span className="flex items-center gap-1.5 font-medium text-foreground/80">
          <span className="size-2.5 shrink-0 rounded-full" style={{ background: GREEN_HEX }} />
          Compliance Score
        </span>
      </div>
      <div id="lowest-compliance-chart" data-name={sectionTitle} className="flex flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col justify-center gap-2.5 overflow-y-auto py-1">
          {barData.map((d) => (
            <Tip
              key={d.name}
              side="top"
              className="flex items-center gap-3"
              label={
                <div className="space-y-0.5">
                  <p className="font-semibold text-foreground">{d.name}</p>
                  <p style={{ color: d.color }}>
                    Compliance: <span className="font-semibold">{d.compliance}%</span> ({d.band})
                  </p>
                  <p className="text-muted-foreground">
                    Scored across <span className="font-semibold text-foreground">{d.applicableControls}</span> applicable controls
                  </p>
                  <p className="text-muted-foreground">
                    Critical findings: <span className="font-semibold text-foreground">{d.criticalFindings}</span>
                  </p>
                  {d.coveragePct != null && d.coveragePct < LOW_COVERAGE_THRESHOLD && (
                    <p className="text-amber-600 dark:text-amber-400">
                      {d.coveragePct}% assessment coverage
                    </p>
                  )}
                </div>
              }
            >
              <span className="w-[132px] shrink-0 text-right text-xs font-medium leading-tight text-foreground/80">
                {d.short}
              </span>
              <div className="relative h-3 flex-1 overflow-hidden rounded-full bg-foreground/[0.08]">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${d.compliance}%`, background: GREEN_HEX }}
                />
              </div>
              <span className="w-9 shrink-0 whitespace-nowrap text-right text-xs font-bold tabular-nums text-foreground">
                {d.compliance}%
              </span>
            </Tip>
          ))}
          {barData.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
              <p className="text-sm font-semibold text-foreground">No compliance scores to rank</p>
              <p className="max-w-xs text-xs text-muted-foreground">
                None of the {allRows.length} {entityLabelPlural.toLowerCase()} in {scopeLabel} has a published
                application with an applicable control set.
              </p>
            </div>
          )}
        </div>
        {unmeasuredCount > 0 && (
          <p className="mt-1 shrink-0 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{unmeasuredCount}</span>{" "}
            {unmeasuredCount === 1 ? entityLabel.toLowerCase() : entityLabelPlural.toLowerCase()} not ranked:
            no compliance score has been produced yet.
          </p>
        )}
      </div>

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1160px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-lg font-semibold text-foreground">{entityLabelPlural} by Compliance Score</h2>
            <p className="mt-1 text-sm text-muted-foreground">All {allRows.length} {entityLabelPlural.toLowerCase()}, ranked lowest compliance first</p>
            <DrawerSearch
              value={sort.query}
              onChange={(v) => {
                sort.setQuery(v);
                setDrawerPage(0);
              }}
              placeholder={`Search ${entityLabelPlural.toLowerCase()}`}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <DataTable<OrgRiskRow>
              columns={drawerColumns}
              data={drawerRows}
              rowKey={(r) => r.id}
              emptyText="No Data Available"
              page={drawerPage}
              pageSize={PAGE_SIZE}
              totalItems={sort.sorted.length}
              onPageChange={setDrawerPage}
              itemLabel={entityLabelPlural.toLowerCase()}
              sortKey={sort.sortKey}
              sortDir={sort.sortDir}
              onSort={(k) => {
                sort.onSort(k);
                setDrawerPage(0);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}

function ViewMoreButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-0.5 flex shrink-0 items-center gap-1.5 rounded-full border border-indigo-500/50 px-3.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
    >
      View More
      <ArrowRight className="size-3.5" />
    </button>
  );
}

function LeaderboardKpiTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3">
      <p className="text-2xl font-bold tabular-nums text-foreground">{value}</p>
      <p className="mt-0.5 text-xs font-medium text-muted-foreground">{label}</p>
    </div>
  );
}

export function HighestFailedControlsSection({
  allRows,
  entityLabel = "Organization",
  scopeLabel = "the group",
  title,
  subtitle,
}: {
  allRows: OrgRiskRow[];
  entityLabel?: string;
  scopeLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const entityLabelPlural = `${entityLabel}s`;
  const [drawerOrg, setDrawerOrg] = useState<OrgRiskRow | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(0);

  const totalFailed = allRows.reduce((a, r) => a + r.failedControls, 0);
  const withFailures = allRows.filter((r) => r.failedControls > 0);
  const byFailedDesc = [...withFailures].sort((a, b) => b.failedControls - a.failedControls);
  const top5Rows = byFailedDesc.slice(0, 5);
  const top5Sum = top5Rows.reduce((a, r) => a + r.failedControls, 0);
  const remainder = Math.max(0, totalFailed - top5Sum);

  const donutSlices = [
    ...top5Rows.map((r, i) => ({
      label: r.name,
      value: r.failedControls,
      pct: Math.round((r.failedControls / (totalFailed || 1)) * 1000) / 10,
      color: i < CONTRIBUTOR_COLORS.length ? CONTRIBUTOR_COLORS[i] : UNMEASURED_HEX,
    })),
    ...(remainder > 0
      ? [{
          label: `Other ${entityLabelPlural.toLowerCase()} (${byFailedDesc.length - top5Rows.length})`,
          value: remainder,
          pct: Math.round((remainder / (totalFailed || 1)) * 1000) / 10,
          color: UNMEASURED_HEX,
        }]
      : []),
  ];

  const sort = useTableSort(byFailedDesc, { riskLevel: (r) => r.riskScore }, (r) => r.name);
  const drawerRows = sort.sorted.slice(drawerPage * PAGE_SIZE, drawerPage * PAGE_SIZE + PAGE_SIZE);
  const drawerColumns: Column<OrgRiskRow>[] = [
    { key: "name", header: entityLabel, align: "left", headerAlign: "center", sortable: true, render: (r) => <OrgNameCell name={r.name} active={r.active} /> },
    { key: "failedControls", header: "Not Satisfied", sortable: true, render: (r) => <CountCell value={r.failedControls} /> },
    { key: "riskLevel", header: "Risk Level", sortable: true, render: (r) => <RiskBadge score={r.riskScore} /> },
  ];

  const trendLabel = (pct: number | null) =>
    pct == null ? "Not measured" : `${pct > 0 ? "+" : ""}${pct}%`;
  const detailRows = drawerOrg
    ? [
        { metric: "Failed Controls", value: String(drawerOrg.failedControls) },
        { metric: "Attention Priority Index", value: `${drawerOrg.riskScore} of 100` },
        {
          metric: "Compliance Score",
          value: drawerOrg.complianceScore == null ? NOT_MEASURED : `${drawerOrg.complianceScore}%`,
        },
        { metric: "Risk Level", value: riskLevel(drawerOrg.riskScore).label },
        { metric: "Total Audit Findings", value: String(drawerOrg.totalFindings) },
        { metric: "Critical Controls Failed", value: String(drawerOrg.criticalControlsFailed) },
        { metric: "Critical Findings", value: String(drawerOrg.criticalFindings) },
        { metric: "Overdue Remediation", value: String(drawerOrg.overdueRemediation) },
        { metric: "Pending Assessments", value: String(drawerOrg.pendingAssessments) },
        {
          metric: "Trend vs. Previous Quarter",
          value: trendLabel(failedControlsTrendPct(drawerOrg)),
        },
        {
          metric: "Last Audit",
          value:
            drawerOrg.lastAuditDaysAgo == null
              ? "No completed assessment"
              : `${drawerOrg.lastAuditDaysAgo} days ago`,
        },
      ]
    : [];

  const sectionTitle = title ?? `${entityLabelPlural} with Highest Failed Controls`;

  return (
    <Card id="highest-failed-controls-section" name={sectionTitle} className="flex h-full flex-col overflow-hidden">
      <div className="flex items-start justify-between gap-2">
        <SectionTitle
          title={sectionTitle}
          subtitle={
            subtitle ??
            `${entityLabelPlural} ranked by the number of controls currently assessed as Not Satisfied.`
          }
        />
        <ViewMoreButton onClick={() => setViewAllOpen(true)} />
      </div>
      <div className="min-h-[308px] w-full flex-1">
        {donutSlices.length === 0 ? (
          <div className="flex h-full min-h-[308px] flex-col items-center justify-center gap-1 text-center">
            <p className="text-sm font-semibold text-foreground">No controls marked Not Satisfied</p>
            <p className="max-w-xs text-xs text-muted-foreground">
              No {entityLabelPlural.toLowerCase()} in {scopeLabel} have a control the latest assessment marked Not Satisfied.
              Check coverage before reading this as a clean result.
            </p>
          </div>
        ) : (
        <DonutPanel
          id="highest-failed-controls-donut"
          name={sectionTitle}
          slices={donutSlices}
          centerValue={totalFailed}
          centerLabel="Failed Controls"
          legendPerPage={2}
          innerRadius={74}
          outerRadius={106}
          onSliceClick={(slice) => {
            const org = top5Rows.find((r) => r.name === slice.label);
            if (org) setDrawerOrg(org);
            else setViewAllOpen(true);
          }}
        />
        )}
      </div>

      <Sheet open={!!drawerOrg} onOpenChange={(open) => !open && setDrawerOrg(null)}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[960px]">
          {drawerOrg && (
            <>
              <div className="shrink-0 border-b border-border p-6">
                <h2 className="text-lg font-semibold text-foreground">
                  {entityLabel} Control Failure Overview: {drawerOrg.name}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Analyze failed controls, compliance posture, and remediation priorities for the selected{" "}
                  {entityLabel.toLowerCase()}.
                </p>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                <EntityDetailBreakdown row={drawerOrg} trendLabel={trendLabel} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1160px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-lg font-semibold text-foreground">{entityLabelPlural} by Failed Controls</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Compare {entityLabelPlural.toLowerCase()} based on the total number of failed controls and their
              associated risk level.
            </p>
            <DrawerSearch
              value={sort.query}
              onChange={(v) => {
                sort.setQuery(v);
                setDrawerPage(0);
              }}
              placeholder={`Search ${entityLabelPlural.toLowerCase()}`}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <DataTable<OrgRiskRow>
              columns={drawerColumns}
              data={drawerRows}
              rowKey={(r) => r.id}
              emptyText="No Data Available"
              page={drawerPage}
              pageSize={PAGE_SIZE}
              totalItems={sort.sorted.length}
              onPageChange={setDrawerPage}
              itemLabel={entityLabelPlural.toLowerCase()}
              sortKey={sort.sortKey}
              sortDir={sort.sortDir}
              onSort={(k) => {
                sort.onSort(k);
                setDrawerPage(0);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}

const ALL_SEVERITIES: ControlFailureRow["severity"][] = ["Critical", "High", "Medium", "Low", "Unrated"];

export function ControlsRequiringAttentionSection({
  rows,
  allRows,
  entityLabel = "Organization",
  title,
  subtitle,
}: {
  rows: ControlFailureRow[];
  allRows: ControlFailureRow[];
  entityLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const entityLabelPlural = `${entityLabel}s`;
  const [selectedControl, setSelectedControl] = useState<ControlFailureRow | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(0);
  const drawerSort = useTableSort(
    allRows,
    { severity: (r) => severityRank(r.severity) },
    (r) => `${r.controlId} ${r.title} ${r.category}`,
  );
  const drawerRows = drawerSort.sorted.slice(drawerPage * PAGE_SIZE, drawerPage * PAGE_SIZE + PAGE_SIZE);
  const drawerColumns: Column<ControlFailureRow>[] = [
    {
      key: "controlId",
      header: "Control",
      align: "left",
      headerAlign: "center",
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">{r.controlId.toUpperCase()}</p>
          <p className="truncate text-xs text-muted-foreground">{r.title}</p>
        </div>
      ),
    },
    { key: "category", header: "Category", align: "left", headerAlign: "center", sortable: true },
    {
      key: "severity",
      header: "Severity",
      sortable: true,
      render: (r) => <SeverityBadge severity={r.severity} />,
    },
    { key: "orgsAffected", header: `${entityLabelPlural} Affected`, sortable: true, render: (r) => <CountCell value={r.orgsAffected} /> },
  ];
  const data = rows.map((r) => ({
    controlId: r.controlId,
    title: r.title,
    severity: r.severity,
    orgsAffected: r.orgsAffected,
    fill: severityHex(r.severity),
    row: r,
  }));
  const maxOrgsAffected = Math.max(1, ...data.map((d) => d.orgsAffected));
  const cardSort = useTableSort(data, {
    control: (d) => d.controlId,
    severity: (d) => severityRank(d.severity),
  });

  const columns: Column<(typeof data)[number]>[] = [
    {
      key: "control",
      header: "Control",
      align: "left",
      headerAlign: "center",
      sortable: true,
      render: (d) => (
        <div className="flex items-center gap-3">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white"
            style={{ background: d.fill }}
          >
            {d.controlId.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground">{d.controlId.toUpperCase()}</p>
            <p className="text-xs leading-snug text-muted-foreground">{d.title}</p>
          </div>
        </div>
      ),
    },
    {
      key: "severity",
      header: "Severity",
      sortable: true,
      render: (d) => <SeverityBadge severity={d.severity} hex={d.fill} />,
    },
    {
      key: "orgsAffected",
      header: `${entityLabelPlural} Affected`,
      sortable: true,
      render: (d) => (
        <div className="flex items-center gap-2.5">
          <span className="w-9 shrink-0 whitespace-nowrap text-right text-sm font-bold tabular-nums text-foreground">
            {d.orgsAffected}
          </span>
          <div className="h-2 w-full min-w-[40px] overflow-hidden rounded-full bg-foreground/[0.06]">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(d.orgsAffected / maxOrgsAffected) * 100}%`, background: d.fill }}
            />
          </div>
        </div>
      ),
    },
  ];

  const sectionTitle = title ?? "Controls Requiring Immediate Attention";

  return (
    <Card id="controls-requiring-attention-section" name={sectionTitle} className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2">
        <SectionTitle
          title={sectionTitle}
          subtitle={subtitle ?? `Systemic weaknesses affecting multiple ${entityLabelPlural.toLowerCase()}`}
        />
        <ViewMoreButton onClick={() => setViewAllOpen(true)} />
      </div>
      <DataTable
        columns={columns}
        data={cardSort.sorted}
        rowKey={(d) => d.controlId}
        emptyText="No Data Available"
        onRowClick={(d) => {
          setSelectedControl(d.row);
          setViewAllOpen(true);
        }}
        hidePageButtons
        compact
        maxHeight="320px"
        sortKey={cardSort.sortKey}
        sortDir={cardSort.sortDir}
        onSort={cardSort.onSort}
      />

      <Sheet
        open={viewAllOpen}
        onOpenChange={(open) => {
          setViewAllOpen(open);
          if (!open) setSelectedControl(null);
        }}
      >
        <SheetContent side="right" className="flex h-full max-h-screen w-[96vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1160px]">
          {selectedControl ? (
            <>
              <div
                className="shrink-0 border-b border-border p-6"
                style={{ borderLeftWidth: 3, borderLeftColor: severityHex(selectedControl.severity) }}
              >
                <button
                  onClick={() => setSelectedControl(null)}
                  className="mb-3 flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronLeft className="size-4" /> Back to all controls
                </button>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-foreground">
                    Control Impact Summary: {selectedControl.controlId.toUpperCase()}
                    {selectedControl.title ? ` – ${selectedControl.title}` : ""}
                  </h2>
                  <span
                    className="rounded-full px-2 py-0.5 text-xs font-semibold"
                    style={{ background: `${severityHex(selectedControl.severity)}1f`, color: severityHex(selectedControl.severity) }}
                  >
                    {severityDisplayLabel(selectedControl.severity)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  View {entityLabelPlural.toLowerCase()} affected by this control failure and assess its overall
                  compliance impact.
                </p>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold tabular-nums" style={{ color: severityHex(selectedControl.severity) }}>
                    {selectedControl.orgsAffected}
                  </span>
                  <span className="text-xs text-muted-foreground">{entityLabelPlural.toLowerCase()} affected</span>
                </div>
              </div>
              <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-2 overflow-y-auto p-6 sm:grid-cols-2">
                {selectedControl.affectedOrgNames.map((name, idx) => (
                  <Tip key={name} label={name} side="top" className="flex">
                    <div className="flex min-h-11 w-full items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
                      <span className="w-8 shrink-0 whitespace-nowrap text-right text-xs font-semibold tabular-nums text-muted-foreground">
                        {idx + 1}
                      </span>
                      <span className="min-w-0 flex-1 text-sm font-medium leading-snug text-foreground">{name}</span>
                    </div>
                  </Tip>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="shrink-0 border-b border-border p-6">
                <h2 className="text-lg font-semibold text-foreground">All Controls Not Satisfied</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  All {allRows.length} controls marked Not Satisfied in at least one {entityLabel.toLowerCase()} in scope.
                  Controls satisfied everywhere, and controls no assessment reached, are not listed.
                </p>
                <DrawerSearch
                  value={drawerSort.query}
                  onChange={(v) => {
                    drawerSort.setQuery(v);
                    setDrawerPage(0);
                  }}
                  placeholder="Search controls"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                <DataTable<ControlFailureRow>
                  columns={drawerColumns}
                  data={drawerRows}
                  rowKey={(r) => r.id}
                  emptyText="No Data Available"
                  onRowClick={(r) => setSelectedControl(r)}
                  page={drawerPage}
                  pageSize={PAGE_SIZE}
                  totalItems={drawerSort.sorted.length}
                  onPageChange={setDrawerPage}
                  itemLabel="controls"
                  sortKey={drawerSort.sortKey}
                  sortDir={drawerSort.sortDir}
                  onSort={(k) => {
                    drawerSort.onSort(k);
                    setDrawerPage(0);
                  }}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  );
}

function ControlTreemapTile(props: any) {
  const { x, y, width, height, controlId, failureRatePct, fill, row, onSelect, isDark } = props;
  if (!width || !height || !fill) return null;
  const showId = width > 44 && height > 28;
  const showRate = width > 44 && height > 46;
  // Tiles too small for the ID + rate pair still get the rate alone, in the
  // smallest legible size, so every tile shows *something* instead of going blank.
  const showRateOnly = !showId && width > 16 && height > 11;
  const geometryTransition = "transform 250ms ease, width 250ms ease, height 250ms ease";
  // Dark theme reads the same fillOpacity/strokeOpacity as noticeably more
  // saturated against a near-black background, so both are toned down slightly.
  const fillOpacity = isDark ? 0.12 : 0.16;
  const strokeOpacity = isDark ? 0.35 : 0.45;
  return (
    <g
      onClick={() => row && onSelect?.(row)}
      className="cursor-pointer"
      style={{ transform: `translate(${x}px, ${y}px)`, transition: geometryTransition }}
    >
      <rect
        x={1.5}
        y={1.5}
        width={Math.max(0, width - 3)}
        height={Math.max(0, height - 3)}
        rx={6}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={fill}
        strokeOpacity={strokeOpacity}
        strokeWidth={1}
        className="hover:opacity-80"
        style={{ transition: `${geometryTransition}, opacity 200ms ease` }}
      />
      {showId && (
        <text
          x={width / 2}
          y={height / 2 - (showRate ? 7 : 0)}
          textAnchor="middle"
          fill={fill}
          fontSize={12}
          fontWeight={700}
        >
          {controlId}
        </text>
      )}
      {showRate && (
        <text
          x={width / 2}
          y={height / 2 + 12}
          textAnchor="middle"
          fill={fill}
          fontSize={11}
          fontWeight={600}
        >
          {failureRatePct}%
        </text>
      )}
      {showRateOnly && (
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={fill}
          fontSize={9}
          fontWeight={700}
        >
          {failureRatePct}%
        </text>
      )}
    </g>
  );
}

function ControlTreemapTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    payload: { controlId: string; title: string; category: string; severity: string; failureRatePct: number; orgsAffected: number };
  }>;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3.5 py-2.5 text-xs leading-relaxed shadow-xl">
      <p className="font-semibold text-foreground">{p.controlId}: {p.title}</p>
      <p className="text-muted-foreground">{p.category}</p>
      <div className="mt-2 space-y-1 border-t border-border/70 pt-2">
        <p className="text-muted-foreground">
          Failure rate: <span className="font-semibold text-foreground">{p.failureRatePct}%</span>
        </p>
        <p className="text-muted-foreground">
          Orgs affected: <span className="font-semibold text-foreground">{p.orgsAffected}</span>
        </p>
        <p className="text-muted-foreground">
          Severity: <span className="font-semibold text-foreground">{severityDisplayLabel(p.severity)}</span>
        </p>
      </div>
    </div>
  );
}

export function ControlsHighestFailureRateSection({
  rows,
  entityLabel = "Organization",
  scopeLabel = "all organizations",
  title,
  subtitle,
}: {
  rows: ControlFailureRow[];
  entityLabel?: string;
  scopeLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const entityLabelPlural = `${entityLabel}s`;
  const [activeSeverities, setActiveSeverities] = useState<Set<ControlFailureRow["severity"]>>(
    () => new Set(ALL_SEVERITIES),
  );
  const [selectedControl, setSelectedControl] = useState<ControlFailureRow | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(0);
  const toggleSeverity = (severity: ControlFailureRow["severity"]) => {
    setActiveSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(severity)) next.delete(severity);
      else next.add(severity);
      return next.size === 0 ? new Set(ALL_SEVERITIES) : next;
    });
  };
  const sortedRows = [...rows].sort((a, b) => b.failureRatePct - a.failureRatePct);
 
  const visibleRows = sortedRows.filter((r) => activeSeverities.has(r.severity));
  const data = visibleRows
    .slice(0, HEATMAP_TOP_N)
    .map((r) => ({
      name: r.controlId.toUpperCase(),
      controlId: r.controlId.toUpperCase(),
      title: r.title,
      category: r.category,
      severity: r.severity,
      failureRatePct: r.failureRatePct,
      orgsAffected: r.orgsAffected,
      size: Math.max(r.failureRatePct, 2),
      fill: severityHex(r.severity),
      row: r,
      onSelect: setSelectedControl,
      isDark,
    }));

  const sort = useTableSort(
    sortedRows,
    { severity: (r) => severityRank(r.severity) },
    (r) => `${r.controlId} ${r.title} ${r.category}`,
  );
  const drawerRows = sort.sorted.slice(drawerPage * PAGE_SIZE, drawerPage * PAGE_SIZE + PAGE_SIZE);
  const drawerColumns: Column<ControlFailureRow>[] = [
    {
      key: "controlId",
      header: "Control",
      align: "left",
      headerAlign: "center",
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">{r.controlId.toUpperCase()}</p>
          <p className="truncate text-xs text-muted-foreground">{r.title}</p>
        </div>
      ),
    },
    { key: "category", header: "Category", align: "left", headerAlign: "center", sortable: true },
    {
      key: "severity",
      header: "Severity",
      sortable: true,
      render: (r) => <SeverityBadge severity={r.severity} />,
    },
    {
      key: "failureRatePct",
      header: "Failure Rate",
      sortable: true,
      render: (r) => <span className="whitespace-nowrap text-sm font-bold tabular-nums text-foreground">{r.failureRatePct}%</span>,
    },
    { key: "orgsAffected", header: `${entityLabelPlural} Affected`, sortable: true, render: (r) => <CountCell value={r.orgsAffected} /> },
  ];

  const sectionTitle = title ?? "Controls with Highest Failure Rate";

  return (
    <Card id="controls-highest-failure-rate-section" name={sectionTitle} className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2">
        <SectionTitle
          title={sectionTitle}
          subtitle={
            subtitle ??
            (visibleRows.length > HEATMAP_TOP_N
              ? `The ${HEATMAP_TOP_N} controls ranked highest by the percentage of assessments marked Not Satisfied across all ${entityLabelPlural.toLowerCase()}. Open View More for all ${visibleRows.length}.`
              : `Controls ranked by the percentage of assessments marked Not Satisfied across all ${entityLabelPlural.toLowerCase()}.`)
          }
        />
        <ViewMoreButton onClick={() => setViewAllOpen(true)} />
      </div>
      <div
        id="controls-highest-failure-rate-legend"
        data-name={`${sectionTitle} legend`}
        className="-mt-2 mb-2 grid grid-cols-3 gap-x-4 gap-y-1.5 sm:grid-cols-5"
      >
        {ALL_SEVERITIES.map((severity) => {
          const hex = severityHex(severity);
          const active = activeSeverities.has(severity);
          return (
            <button
              key={severity}
              type="button"
              onClick={() => toggleSeverity(severity)}
              className={`flex items-center gap-1.5 overflow-hidden rounded-md px-1 py-0.5 text-left transition-opacity ${active ? "opacity-100" : "opacity-40"}`}
            >
              <span className="size-2.5 shrink-0 rounded-full" style={{ background: hex }} />
              <span className="truncate text-xs font-medium text-foreground/80">{severityDisplayLabel(severity)}</span>
            </button>
          );
        })}
      </div>
      <div id="controls-highest-failure-rate-chart" data-name={sectionTitle} className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={data}
            dataKey="size"
            aspectRatio={4 / 3}
            stroke="none"
            isAnimationActive={false}
            content={<ControlTreemapTile />}
          >
            <Tooltip content={<ControlTreemapTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </div>

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1160px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-lg font-semibold text-foreground">Controls with Failed Assessments</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Detailed list of controls that failed in one or more {entityLabelPlural.toLowerCase()}, including
              severity, failure rate, and organizational impact.
            </p>
            <DrawerSearch
              value={sort.query}
              onChange={(v) => {
                sort.setQuery(v);
                setDrawerPage(0);
              }}
              placeholder="Search controls"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <DataTable<ControlFailureRow>
              columns={drawerColumns}
              data={drawerRows}
              rowKey={(r) => r.id}
              emptyText="No Data Available"
              onRowClick={(r) => setSelectedControl(r)}
              sortKey={sort.sortKey}
              sortDir={sort.sortDir}
              onSort={(k) => {
                sort.onSort(k);
                setDrawerPage(0);
              }}
              page={drawerPage}
              pageSize={PAGE_SIZE}
              totalItems={sort.sorted.length}
              onPageChange={setDrawerPage}
              itemLabel="controls"
            />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!selectedControl} onOpenChange={(open) => !open && setSelectedControl(null)}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[96vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[960px]">
          {selectedControl && (
            <>
              <div
                className="shrink-0 border-b border-border p-6"
                style={{ borderLeftWidth: 3, borderLeftColor: severityHex(selectedControl.severity) }}
              >
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-foreground">
                    Control Impact Summary: {selectedControl.controlId.toUpperCase()}
                    {selectedControl.title ? ` – ${selectedControl.title}` : ""}
                  </h2>
                  <span
                    className="rounded-full px-2 py-0.5 text-xs font-semibold"
                    style={{ background: `${severityHex(selectedControl.severity)}1f`, color: severityHex(selectedControl.severity) }}
                  >
                    {severityDisplayLabel(selectedControl.severity)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  View {entityLabelPlural.toLowerCase()} affected by this control failure and assess its overall
                  compliance impact.
                </p>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold tabular-nums" style={{ color: severityHex(selectedControl.severity) }}>
                    {selectedControl.orgsAffected}
                  </span>
                  <span className="text-xs text-muted-foreground">{entityLabelPlural.toLowerCase()} affected</span>
                </div>
              </div>
              <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-2 overflow-y-auto p-6 sm:grid-cols-2">
                {(selectedControl.affectedOrgNames ?? []).map((name, idx) => (
                  <Tip key={name} label={name} side="top" className="flex">
                    <div className="flex min-h-11 w-full items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
                      <span className="w-8 shrink-0 whitespace-nowrap text-right text-xs font-semibold tabular-nums text-muted-foreground">
                        {idx + 1}
                      </span>
                      <span className="min-w-0 flex-1 text-sm font-medium leading-snug text-foreground">{name}</span>
                    </div>
                  </Tip>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  );
}

export function FailureReasonsSection({
  rows,
  entityLabel = "Organization",
  title,
  subtitle,
}: {
  rows: FailureReasonRow[];
  entityLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const entityLabelPlural = `${entityLabel}s`;
  const sectionTitle = title ?? `Top Failure Reasons Across ${entityLabelPlural}`;
  return (
    <Card id="failure-reasons-section" name={sectionTitle} className="flex h-full flex-col">
      <SectionTitle title={sectionTitle} subtitle={subtitle ?? "Why failures occur, not just what failed"} />
      <div id="failure-reasons-chart" data-name={sectionTitle} className="flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.reason} className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground/85">{r.reason}</span>
              <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                {r.count} ({r.pct}%)
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-foreground/[0.06]">
              <div className="h-full rounded-full bg-[#8b5cf6] transition-all duration-500" style={{ width: `${r.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PendingCompletedTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { name: string; pending: number; completed: number } }>;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{p.name}</p>
      <p className="text-muted-foreground">
        Pending: <span className="font-semibold text-foreground">{p.pending}</span>
      </p>
      <p className="text-muted-foreground">
        Completed: <span className="font-semibold text-foreground">{p.completed}</span>
      </p>
    </div>
  );
}

function RepeatedFailureTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { name: string; total: number; failedControls: number; deepest: number } }>;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{p.name}</p>
      <p className="mt-0.5 text-muted-foreground">
        <span className="font-semibold text-foreground">{p.total}</span> repeat controls of{" "}
        <span className="font-semibold text-foreground">{p.failedControls}</span> currently failing
      </p>
      <p className="text-muted-foreground">
        Deepest streak <span className="font-semibold text-foreground">{p.deepest} cycles</span>
      </p>
      <p className="mt-1 border-t border-border pt-1 text-muted-foreground">Click for the control list</p>
    </div>
  );
}

function RepeatTotalLabel(props: any) {
  const { x, y, width, height, index, data } = props;
  if (x == null || y == null || width == null || height == null) return null;
  const d = data?.[index];
  if (!d) return null;
  const pct = d.failedControls > 0 ? Math.round((d.total / d.failedControls) * 100) : 0;
  return (
    <text x={x + width + 8} y={y + height / 2} dy={4} fontSize={11} fontWeight={700} className="fill-foreground">
      {d.total}
      <tspan className="fill-muted-foreground" fontWeight={500}> ({pct}%)</tspan>
    </text>
  );
}

const REPEATED_FAILURES_TOP_N = 5;

export function RepeatedControlFailuresSection({
  allRows,
  entityLabel = "Organization",
  title,
  subtitle,
}: {
  allRows: OrgRiskRow[];
  entityLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const entityLabelPlural = `${entityLabel}s`;
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(0);
  const [drilldown, setDrilldown] = useState<OrgRiskRow | null>(null);

  const bucketsOf = (r: OrgRiskRow) => {
    const counts: Record<string, number> = {};
    REPEAT_STREAK_BUCKETS.forEach((b) => (counts[b.key] = 0));
    r.repeatedFailureControls.forEach((c) => {
      counts[repeatStreakBucket(c.consecutiveCycles).key] += 1;
    });
    return counts;
  };

  const top = allRows.slice(0, REPEATED_FAILURES_TOP_N);
  const chartData = top.map((r) => ({
    name: r.name,
    row: r,
    total: r.repeatedFailureControls.length,
    failedControls: r.failedControls,
    deepest: r.repeatedFailureControls[0]?.consecutiveCycles ?? 0,
    ...bucketsOf(r),
  }));

  const chronicEntities = allRows.length;
  const chronicControls = allRows.reduce((sum, r) => sum + r.repeatedFailureControls.length, 0);
  const entrenched = allRows.reduce(
    (sum, r) => sum + r.repeatedFailureControls.filter((c) => c.consecutiveCycles >= 4).length,
    0,
  );

  const drawerColumns: Column<OrgRiskRow>[] = [
    { key: "name", header: entityLabel, align: "left", headerAlign: "center", render: (r) => <OrgNameCell name={r.name} active={r.active} /> },
    { key: "repeated", header: "Repeat Controls", render: (r) => <CountCell value={r.repeatedFailureControls.length} /> },
    { key: "failedControls", header: "Currently Failing", render: (r) => <CountCell value={r.failedControls} /> },
    {
      key: "share",
      header: "Share Chronic",
      render: (r) => (
        <span className="text-sm font-semibold tabular-nums text-foreground/80">
          {r.failedControls > 0 ? Math.round((r.repeatedFailureControls.length / r.failedControls) * 100) : 0}%
        </span>
      ),
    },
    {
      key: "deepest",
      header: "Deepest Streak",
      render: (r) => {
        const deepest = r.repeatedFailureControls[0]?.consecutiveCycles ?? 0;
        const hex = repeatStreakBucket(deepest).hex;
        return (
          <span className="text-sm font-bold tabular-nums" style={{ color: hex }}>
            {deepest} cycles
          </span>
        );
      },
    },
  ];
  const drawerRows = allRows.slice(drawerPage * PAGE_SIZE, drawerPage * PAGE_SIZE + PAGE_SIZE);

  const controlColumns: Column<RepeatedControlFailure>[] = [
    { key: "controlId", header: "Control", align: "left", headerAlign: "left", render: (c) => (
      <span className="text-left text-sm font-semibold text-foreground">{c.controlId}</span>
    ) },
    { key: "title", header: "Title", align: "left", headerAlign: "left", render: (c) => (
      <span className="text-left text-sm text-foreground/80">{c.title}</span>
    ) },
    { key: "severity", header: "Severity", render: (c) => <SeverityBadge severity={c.severity} /> },
    { key: "consecutiveCycles", header: "Consecutive Cycles", render: (c) => (
      <span className="text-sm font-bold tabular-nums" style={{ color: repeatStreakBucket(c.consecutiveCycles).hex }}>
        {c.consecutiveCycles}
      </span>
    ) },
    { key: "since", header: "Failing Since", render: (c) => (
      <span className="text-sm tabular-nums text-muted-foreground">{c.since}</span>
    ) },
  ];

  const sectionTitle = title ?? `${entityLabelPlural} with Repeated Control Failures`;

  return (
    <Card id="repeated-control-failures-section" name={sectionTitle} className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2">
        <SectionTitle
          title={sectionTitle}
          subtitle={
            subtitle ??
            `Same control failed in two or more consecutive assessment cycles, counted and ranked`
          }
        />
        <ViewMoreButton onClick={() => setViewAllOpen(true)} />
      </div>

      {allRows.length === 0 ? (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 py-10 text-center">
          <ShieldCheck className="size-8" style={{ color: GREEN_HEX }} />
          <p className="text-sm font-semibold text-foreground">No repeated control failures</p>
          <p className="max-w-md text-sm text-muted-foreground">
            No {entityLabelPlural.toLowerCase()} carry a control that failed in two consecutive cycles. Remediation is
            holding.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-baseline gap-1.5 rounded-lg bg-foreground/[0.05] px-2.5 py-1 text-xs font-semibold text-muted-foreground">
              <span className="text-base font-bold leading-none tabular-nums text-foreground">{chronicEntities}</span>
              {entityLabelPlural.toLowerCase()} affected
            </span>
            <span className="inline-flex items-baseline gap-1.5 rounded-lg bg-foreground/[0.05] px-2.5 py-1 text-xs font-semibold text-muted-foreground">
              <span className="text-base font-bold leading-none tabular-nums text-foreground">{chronicControls}</span>
              chronic controls
            </span>
            <Tip
              label="Controls that have failed four or more cycles running. Remediation for these is demonstrably not working."
              side="top"
            >
              <span
                className="inline-flex items-baseline gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold"
                style={{ background: "#b91c1c14", color: "#b91c1c" }}
              >
                <span className="text-base font-bold leading-none tabular-nums">{entrenched}</span>
                entrenched (4+ cycles)
              </span>
            </Tip>
          </div>

          <div
            id="repeated-control-failures-legend"
            data-name={`${sectionTitle} legend`}
            className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5"
          >
            {REPEAT_STREAK_BUCKETS.map((b) => (
              <span key={b.key} className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
                <span className="size-2.5 shrink-0 rounded-full" style={{ background: b.hex }} />
                {b.label}
              </span>
            ))}
          </div>

          <div
            id="repeated-control-failures-chart"
            data-name={sectionTitle}
            className="mt-1 w-full flex-1"
            style={{ minHeight: chartData.length * 46 + 44 }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 72, left: 8, bottom: 20 }} barCategoryGap="26%">
                <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-muted-foreground"
                  axisLine={false}
                  tickLine={false}
                  label={{ value: "Controls failing in consecutive cycles", position: "bottom", offset: 0, fontSize: 11, fill: "currentColor" }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-foreground"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                />
                <Tooltip content={<RepeatedFailureTooltip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
                {REPEAT_STREAK_BUCKETS.map((b, i) => (
                  <Bar
                    key={b.key}
                    dataKey={b.key}
                    name={b.label}
                    stackId="repeat"
                    fill={b.hex}
                    maxBarSize={26}
                    radius={i === REPEAT_STREAK_BUCKETS.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                    cursor="pointer"
                    onClick={(d: any) => d?.row && setDrilldown(d.row)}
                  >
                    {i === REPEAT_STREAK_BUCKETS.length - 1 && (
                      <LabelList dataKey={b.key} content={(p: any) => <RepeatTotalLabel {...p} data={chartData} />} />
                    )}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <Sheet open={!!drilldown} onOpenChange={(open) => !open && setDrilldown(null)}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[860px]">
          {drilldown && (
            <>
              <div className="shrink-0 border-b border-border p-6">
                <h2 className="text-xl font-bold text-foreground">{drilldown.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {drilldown.repeatedFailureControls.length} controls failing in consecutive cycles, of{" "}
                  {drilldown.failedControls} currently failing
                </p>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                <DataTable<RepeatedControlFailure>
                  columns={controlColumns}
                  data={drilldown.repeatedFailureControls}
                  rowKey={(c) => c.controlId}
                  emptyText="No Data Available"
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1160px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-xl font-bold text-foreground">{title ?? `${entityLabelPlural} with Repeated Control Failures`}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              All {allRows.length} {entityLabelPlural.toLowerCase()} carrying a control that failed in two or more
              consecutive cycles, ranked by count
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <DataTable<OrgRiskRow>
              columns={drawerColumns}
              data={drawerRows}
              rowKey={(r) => r.id}
              emptyText="No Data Available"
              page={drawerPage}
              pageSize={PAGE_SIZE}
              totalItems={allRows.length}
              onPageChange={setDrawerPage}
              itemLabel={entityLabelPlural.toLowerCase()}
              onRowClick={(r) => setDrilldown(r)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}

const PENDING_HEX = "#f59e0b";
const COMPLETED_HEX = GREEN_HEX;

function StackSegment(props: any) {
  if (!props?.height) return null;
  return <Rectangle {...props} />;
}

function ActiveStackSegment(props: any) {
  const { x, y, width, height, fill, radius } = props ?? {};
  if (x == null || y == null || !width || !height) return null;
  const r = Array.isArray(radius) ? radius[0] ?? 0 : 0;
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      rx={r}
      fill={fill}
      fillOpacity={0.36}
      stroke={fill}
      strokeWidth={2}
    />
  );
}


const PENDING_ASSESSMENTS_TOP_N = 7;

function countAxis(max: number, targetIntervals = 4): { max: number; ticks: number[] } {
  if (!Number.isFinite(max) || max <= 0) return { max: 1, ticks: [0, 1] };
  const magnitude = Math.pow(10, Math.floor(Math.log10(max / targetIntervals || 1)));
  const normalized = max / targetIntervals / magnitude;
  const rawStep = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
  const step = Math.max(1, Math.round(rawStep));
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= top; t += step) ticks.push(t);
  return { max: top, ticks };
}

export function MaxPendingAssessmentsSection({
  allRows,
  entityLabel = "Organization",
  title,
  subtitle,
}: {
  allRows: OrgRiskRow[];
  entityLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const entityLabelPlural = `${entityLabel}s`;
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(0);
  const [drilldownOrg, setDrilldownOrg] = useState<OrgRiskRow | null>(null);
  const [drilldownStatusFilter, setDrilldownStatusFilter] = useState<"All Status" | "Pending" | "Completed">("All Status");

  const rowsWithAssessments = allRows.filter((r) => r.pendingAssessments + r.completedAssessments > 0);
  const byPendingDesc = [...rowsWithAssessments].sort(
    (a, b) =>
      b.pendingAssessments - a.pendingAssessments ||
      b.completedAssessments - a.completedAssessments ||
      a.name.localeCompare(b.name),
  );
  const topRanked = byPendingDesc.slice(0, PENDING_ASSESSMENTS_TOP_N);
  const chartData = topRanked.map((r) => ({
    name: r.name,
    pending: r.pendingAssessments,
    completed: r.completedAssessments,
    total: r.pendingAssessments + r.completedAssessments,
  }));

  const assessmentAxis = countAxis(Math.max(0, ...chartData.map((d) => d.total)));

  const [chartRef, chartWidth] = useElementWidth<HTMLDivElement>();
  const chartNames = chartData.map((d) => d.name);
  const bandWidth =
    chartWidth > 0 && chartNames.length ? Math.max(28, Math.floor((chartWidth - 76) / chartNames.length)) : 120;
  const namesKey = chartNames.join("|");
  const axisLabels = useMemo(
    () => orgAxisLabelLayout(namesKey ? namesKey.split("|") : [], bandWidth),
    [namesKey, bandWidth],
  );
  const tickAreaHeight = axisLabels.height;

  const StackTotalLabel = (props: any) => {
    const { x, y, width, index } = props;
    if (x == null || y == null || width == null || index == null) return null;
    if (!chartData[index]?.total) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        fontSize={12}
        fontWeight={700}
        className="fill-black dark:fill-white"
      >
        {chartData[index]?.total}
      </text>
    );
  };

  const sort = useTableSort(byPendingDesc, { riskLevel: (r) => r.riskScore }, (r) => r.name);
  const drawerRows = sort.sorted.slice(drawerPage * PAGE_SIZE, drawerPage * PAGE_SIZE + PAGE_SIZE);
  const drawerColumns: Column<OrgRiskRow>[] = [
    { key: "name", header: entityLabel, align: "left", headerAlign: "center", sortable: true, render: (r) => <OrgNameCell name={r.name} active={r.active} /> },
    { key: "pendingAssessments", header: "Pending", sortable: true, render: (r) => <CountCell value={r.pendingAssessments} /> },
    { key: "completedAssessments", header: "Completed", sortable: true, render: (r) => <CountCell value={r.completedAssessments} /> },
    { key: "riskLevel", header: "Risk Level", sortable: true, render: (r) => <RiskBadge score={r.riskScore} /> },
  ];

  const drilldownAssessmentRows: Array<{ assessment: string; status: "Pending" | "Completed" }> = drilldownOrg
    ? [
        ...(drilldownOrg.pendingAssessmentNames ?? []).map((assessment) => ({ assessment, status: "Pending" as const })),
        ...(drilldownOrg.completedAssessmentNames ?? []).map((assessment) => ({ assessment, status: "Completed" as const })),
      ]
    : [];
  const drilldownAssessmentNamesTruncated = drilldownOrg
    ? (drilldownOrg.pendingAssessmentNames ?? []).length < drilldownOrg.pendingAssessments ||
      (drilldownOrg.completedAssessmentNames ?? []).length < drilldownOrg.completedAssessments
    : false;
  const drilldownFilteredRows =
    drilldownStatusFilter === "All Status"
      ? drilldownAssessmentRows
      : drilldownAssessmentRows.filter((r) => r.status === drilldownStatusFilter);
  const drilldownSort = useTableSort(
    drilldownFilteredRows,
    { status: (r) => (r.status === "Pending" ? 0 : 1) },
    (r) => r.assessment,
  );
  const drilldownColumns: Column<(typeof drilldownAssessmentRows)[number]>[] = [
    { key: "assessment", header: "Assessment", align: "left", headerAlign: "center", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (r) => (
        <SeverityBadge
          severity={r.status}
          hex={r.status === "Pending" ? PENDING_HEX : COMPLETED_HEX}
        />
      ),
    },
  ];

  const sectionTitle = title ?? `${entityLabelPlural} with Pending Assessments`;

  return (
    <Card id="max-pending-assessments-section" name={sectionTitle} className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2">
        <SectionTitle
          title={sectionTitle}
          subtitle={
            subtitle ??
            (byPendingDesc.length > topRanked.length
              ? `Compare completed and pending assessments to identify ${entityLabelPlural.toLowerCase()} requiring assessment completion. Top ${topRanked.length} of ${byPendingDesc.length}.`
              : `Compare completed and pending assessments to identify ${entityLabelPlural.toLowerCase()} requiring assessment completion.`)
          }
        />
        <ViewMoreButton onClick={() => setViewAllOpen(true)} />
      </div>
      <div
        id="max-pending-assessments-legend"
        data-name={`${sectionTitle} legend`}
        className="mb-1 mt-1 flex items-center gap-4"
      >
        <span className="flex items-center gap-1.5 text-xs font-medium text-black dark:text-white">
          <span className="size-2.5 shrink-0 rounded-full" style={{ background: COMPLETED_HEX }} /> Completed
        </span>
        <span className="flex items-center gap-1.5 text-xs font-medium text-black dark:text-white">
          <span className="size-2.5 shrink-0 rounded-full" style={{ background: PENDING_HEX }} /> Pending
        </span>
      </div>
      <div
        ref={chartRef}
        id="max-pending-assessments-chart"
        data-name={sectionTitle}
        className="w-full"
        style={{ height: 270 + tickAreaHeight }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 24, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              dataKey="name"
              interval={0}
              tick={<OrgNameAxisTick layout={axisLabels} />}
              className="text-black dark:text-white"
              axisLine={{ stroke: "#94a3b8", strokeOpacity: 0.35 }}
              tickLine={false}
              height={tickAreaHeight}
              label={{ value: entityLabel, position: "insideBottom", offset: 0, fontSize: 11, fill: "currentColor" }}
            />
            <YAxis
              allowDecimals={false}
              domain={[0, assessmentAxis.max]}
              ticks={assessmentAxis.ticks}
              tick={{ fontSize: 12, fill: "currentColor", fontWeight: 600 }}
              className="text-black dark:text-white"
              axisLine={{ stroke: "#94a3b8", strokeOpacity: 0.35 }}
              tickLine={false}
              label={{ value: "Assessments", angle: -90, position: "insideLeft", fontSize: 11, fill: "currentColor" }}
            />
            <Tooltip content={<PendingCompletedTooltip />} cursor={false} />
            <Bar
              dataKey="completed"
              name="Completed"
              stackId="a"
              fill={COMPLETED_HEX}
              fillOpacity={0.18}
              stroke={COMPLETED_HEX}
              strokeWidth={1.5}
              maxBarSize={44}
              shape={<StackSegment />}
              activeBar={<ActiveStackSegment />}
              cursor="pointer"
              onClick={(d: any) => {
                const org = topRanked.find((r) => r.name === d?.name);
                if (org) setDrilldownOrg(org);
              }}
            />
            <Bar
              dataKey="pending"
              name="Pending"
              stackId="a"
              fill={PENDING_HEX}
              fillOpacity={0.18}
              stroke={PENDING_HEX}
              strokeWidth={1.5}
              radius={[4, 4, 0, 0]}
              maxBarSize={44}
              shape={<StackSegment />}
              activeBar={<ActiveStackSegment />}
              cursor="pointer"
              onClick={(d: any) => {
                const org = topRanked.find((r) => r.name === d?.name);
                if (org) setDrilldownOrg(org);
              }}
            >
              <LabelList dataKey="total" content={StackTotalLabel} style={{ pointerEvents: "none" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <Sheet
        open={!!drilldownOrg}
        onOpenChange={(open) => {
          if (!open) {
            setDrilldownOrg(null);
            setDrilldownStatusFilter("All Status");
          }
        }}
      >
        <SheetContent
          side="right"
          overlayClassName={viewAllOpen ? "bg-black/35 backdrop-blur-md" : undefined}
          className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[960px]"
        >
          {drilldownOrg && (
            <>
              <div className="shrink-0 border-b border-border p-6">
                <h2 className="text-lg font-semibold text-foreground">
                  {entityLabel} Assessment Status: {drilldownOrg.name}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  View all assessments for the selected {entityLabel.toLowerCase()}, filtered by completion status
                  and progress.
                </p>
                {drilldownAssessmentNamesTruncated && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Showing {(drilldownOrg.pendingAssessmentNames ?? []).length} of{" "}
                    {drilldownOrg.pendingAssessments} pending and{" "}
                    {(drilldownOrg.completedAssessmentNames ?? []).length} of{" "}
                    {drilldownOrg.completedAssessments} completed by name
                  </p>
                )}
                <div className="mt-3 flex items-center rounded-full border border-border p-0.5 w-fit">
                  {(["All Status", "Pending", "Completed"] as const).map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setDrilldownStatusFilter(status)}
                      className={`flex h-7 min-w-20 items-center justify-center whitespace-nowrap rounded-full px-3 text-xs font-semibold transition-colors ${
                        drilldownStatusFilter === status ? "bg-indigo-600 text-white" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                <DataTable
                  columns={drilldownColumns}
                  data={drilldownSort.sorted}
                  rowKey={(r, i) => `${r.status}:${r.assessment}:${i}`}
                  emptyText="No assessments"
                  sortKey={drilldownSort.sortKey}
                  sortDir={drilldownSort.sortDir}
                  onSort={drilldownSort.onSort}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[92vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1160px]">
          <div
            className={`flex h-full min-h-0 flex-1 flex-col gap-0 transition-all duration-300 ${
              drilldownOrg ? "-translate-x-8 scale-[0.97] opacity-50 pointer-events-none" : ""
            }`}
          >
            <div className="shrink-0 border-b border-border p-6">
              <h2 className="text-lg font-semibold text-foreground">{entityLabelPlural} by Pending Assessments</h2>
              <p className="mt-1 text-sm text-muted-foreground">All {rowsWithAssessments.length} {entityLabelPlural.toLowerCase()} with assessments, ranked largest backlog first</p>
              <DrawerSearch
                value={sort.query}
                onChange={(v) => {
                  sort.setQuery(v);
                  setDrawerPage(0);
                }}
                placeholder={`Search ${entityLabelPlural.toLowerCase()}`}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <DataTable<OrgRiskRow>
                columns={drawerColumns}
                data={drawerRows}
                rowKey={(r) => r.id}
                emptyText="No Data Available"
                onRowClick={(r) => setDrilldownOrg(r)}
                page={drawerPage}
                pageSize={PAGE_SIZE}
                totalItems={sort.sorted.length}
                onPageChange={setDrawerPage}
                itemLabel={entityLabelPlural.toLowerCase()}
                sortKey={sort.sortKey}
                sortDir={sort.sortDir}
                onSort={(k) => {
                  sort.onSort(k);
                  setDrawerPage(0);
                }}
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}

function TrendTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: { period: string; pctPass: number } }> }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{p.period}</p>
      <p className="text-muted-foreground">
        Pass rate: <span className="font-semibold text-foreground">{p.pctPass}%</span>
      </p>
    </div>
  );
}

export function PassFailDistributionSection({
  summary,
  scopeLabel = "Enterprise",
  title,
  subtitle,
}: {
  summary: PassFailSummary;
  scopeLabel?: string;
  title?: string;
  subtitle?: string;
}) {
  const slices = [
    { label: "Passed", value: summary.passed, pct: summary.pctPass, color: GREEN_HEX },
    { label: "Failed", value: summary.failed, pct: Math.round((100 - summary.pctPass) * 10) / 10, color: RED_HEX },
  ];
  const sectionTitle = title ?? `${scopeLabel} Pass vs Fail Distribution`;

  return (
    <Card id="pass-fail-distribution-section" name={sectionTitle} className="flex h-full flex-col">
      <SectionTitle
        title={sectionTitle}
        subtitle={subtitle ?? `${scopeLabel} health indicator and trend baseline`}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="min-h-0">
          <DonutPanel
            id="pass-fail-distribution-donut"
            name={sectionTitle}
            slices={slices}
            centerValue={summary.totalChecks}
            centerLabel="Total Checks"
            legendPerPage={2}
          />
        </div>
        <div className="flex flex-col">
          <p className="mb-2 text-xs font-semibold text-muted-foreground">Pass rate trend</p>
          <div id="pass-rate-trend-chart" data-name="Pass Rate Trend" className="h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summary.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="passRateFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4f8ef7" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#4f8ef7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 12, fill: "currentColor", fontWeight: 600 }}
                  className="text-black dark:text-white"
                  axisLine={{ stroke: "#94a3b8", strokeOpacity: 0.35 }}
                  tickLine={false}
                />
                <Tooltip content={<TrendTooltip />} />
                <Area
                  type="monotone"
                  dataKey="pctPass"
                  stroke="#4f8ef7"
                  strokeWidth={2}
                  fill="url(#passRateFill)"
                  dot={{ r: 3, fill: "#4f8ef7", strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </Card>
  );
}
